'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useState, useEffect } from 'react';
import { subscribeToNewsletter } from '@/services/newsletterService';
import { getContactInfo, getSocialMediaLinks, type ContactInfo, type SocialMediaLinks } from '@/services/contactService';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '+55 11 95114-2887', // Atualizado
    email: 'atendimento@telemarcas.com.br', // Atualizado
    address: 'Rua Professor Antônio Gama de Cerqueira, 121, Centro, São Miguel Paulista, São Paulo, Cep: 08010130', // Atualizado
  });
  const [socialLinks, setSocialLinks] = useState<SocialMediaLinks>({
    whatsapp: 'https://api.whatsapp.com/send?phone=5511951142887',
    instagram: 'https://instagram.com/telemarcas',
    linkedin: 'https://linkedin.com/company/telemarcas',
    facebook: 'https://facebook.com/telemarcas',
    twitter: 'https://twitter.com/telemarcas',
  });

  useEffect(() => {
    const loadContactData = async () => {
      const contact = await getContactInfo();
      const social = await getSocialMediaLinks();
      setContactInfo(contact);
      setSocialLinks(social);
    };
    loadContactData();
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ text: 'Por favor, insira seu e-mail.', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    const result = await subscribeToNewsletter(email);
    
    setMessage({
      text: result.message,
      type: result.success ? 'success' : 'error'
    });

    if (result.success) {
      setEmail('');
    }

    setIsSubmitting(false);

    // Clear message after 5 seconds
    setTimeout(() => setMessage(null), 5000);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-foreground text-background py-16 px-6 lg:px-8">
      <div className="max-w-8xl mx-auto">
        {/* Contact Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8 border-b border-background/20 mb-8">
          {/* Phone */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="PhoneIcon" size={24} className="text-background" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-background mb-2">
                Telefone
              </h3>
              <a
                href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                className="text-background/80 hover:text-background transition-colors"
              >
                {contactInfo.phone}
              </a>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="EnvelopeIcon" size={24} className="text-background" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-background mb-2">
                E-mail
              </h3>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-background/80 hover:text-background transition-colors break-all"
              >
                {contactInfo.email}
              </a>
            </div>
          </div>

          {/* Address */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="MapPinIcon" size={24} className="text-background" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider text-background mb-2">
                Localização
              </h3>
              <p className="text-background/80">
                {contactInfo.address}
              </p>
            </div>
          </div>
        </div>

        {/* Main Footer Row */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 pb-8 border-b border-background/20">
          {/* Logo */}
          <div className="flex flex-col gap-2">
            <Link
              href="/homepage"
              className="text-3xl font-black tracking-tighter uppercase text-background hover:text-primary transition-colors"
            >
              TELEMARCAS
            </Link>
            <p className="text-xs text-background/60 max-w-xs">
              Especialistas em registro de marcas e patentes com mais de 12 anos de experiência.
            </p>
          </div>

          {/* Links */}
          <nav className="flex flex-wrap items-center justify-center gap-8 text-sm font-bold uppercase tracking-wider">
            <button
              onClick={() => scrollToSection('inicio')}
              className="text-background/80 hover:text-background transition-colors"
            >
              Início
            </button>
            <button
              onClick={() => scrollToSection('servicios')}
              className="text-background/80 hover:text-background transition-colors"
            >
              Serviços
            </button>
            <button
              onClick={() => scrollToSection('blog')}
              className="text-background/80 hover:text-background transition-colors"
            >
              Blog
            </button>
            <button
              onClick={() => scrollToSection('contacto')}
              className="text-background/80 hover:text-background transition-colors"
            >
              Contato
            </button>
          </nav>

          {/* Social Icons */}
          <div className="flex items-center gap-3">
            <a
              href={socialLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border-2 border-background/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all"
              aria-label="WhatsApp"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-background" />
            </a>
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border-2 border-background/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all"
              aria-label="Instagram"
            >
              <Icon name="CameraIcon" size={18} className="text-background" />
            </a>
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border-2 border-background/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all"
              aria-label="LinkedIn"
            >
              <Icon name="BuildingOfficeIcon" size={18} className="text-background" />
            </a>
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border-2 border-background/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all"
              aria-label="Facebook"
            >
              <Icon name="UserGroupIcon" size={18} className="text-background" />
            </a>
            <a
              href={socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full border-2 border-background/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-all"
              aria-label="Twitter"
            >
              <Icon name="AtSymbolIcon" size={18} className="text-background" />
            </a>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 py-8 border-b border-background/20">
          <div className="flex-1 text-center lg:text-left">
            <h3 className="text-xl font-black uppercase tracking-tight text-background mb-2">
              Inscreva-se em Nossa Newsletter
            </h3>
            <p className="text-sm text-background/70">
              Receba as últimas notícias sobre marcas e patentes diretamente no seu e-mail.
            </p>
          </div>
          
          <form onSubmit={handleNewsletterSubmit} className="flex-1 w-full max-w-md">
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu e-mail"
                className="flex-1 px-4 py-3 rounded-lg bg-background/10 border-2 border-background/20 text-background placeholder:text-background/50 focus:outline-none focus:border-primary transition-colors"
                disabled={isSubmitting}
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-primary text-background font-bold uppercase tracking-wider rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Inscrever-se'}
              </button>
            </div>
            {message && (
              <p className={`mt-2 text-sm font-medium ${
                message.type === 'success' ? 'text-success' : 'text-error'
              }`}>
                {message.text}
              </p>
            )}
          </form>
        </div>

        {/* Bottom Row */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 text-xs text-background/50 font-bold uppercase tracking-widest">
          <p>© 2026 TELEMARCAS. Todos os direitos reservados.</p>
          <div className="flex items-center gap-6">
            <Link href="#" className="hover:text-background transition-colors">
              Política de Privacidade
            </Link>
            <span>·</span>
            <Link href="#" className="hover:text-background transition-colors">
              Termos de Serviço
            </Link>
            <span>·</span>
            <Link href="/admin" className="hover:text-background transition-colors">
              Admin
            </Link>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href={socialLinks.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 group"
        aria-label="Contato via WhatsApp"
      >
        <svg
          className="w-9 h-9 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span className="absolute -top-1 -left-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></span>
      </a>
    </footer>
  );
}
